import { useEffect, useRef } from "react";
import { useHydrateAtoms } from "jotai/utils";
import type { SectionProps } from "../../../components/Questionnaire/Section";
import type { RuleSet } from "../../../components/Questionnaire/validation/form/types";
import { ACTIONS } from "../reducer/constants";
import type { QuestionnaireState } from "../types";
import { questionnaireReducerAtom } from "../state/questionnaireReducerAtom";
import { computeInitialQuestionnaireState } from "../utils/computeInitialQuestionnaireState";
import { useQuestionnaireActions } from "./useQuestionnaireActions";

/**
 * Initialize questionnaire state without subscribing to changes.
 * Use when a parent already subscribes and you just need to set up state.
 *
 * Hydrates the questionnaire atom synchronously on first render via
 * useHydrateAtoms, then uses useEffect only for subsequent prop changes.
 *
 * @example useQuestionnaireInitialization(sections, validations, config);
 * @see useQuestionnaire - initialize + subscribe to state
 */
export const useQuestionnaireInitialization = (
  sections?: SectionProps[],
  validations?: readonly RuleSet[],
  config: Record<string, unknown> = {},
) => {
  // Compute the initial state once on first render. useRef with lazy
  // initialisation avoids recomputing on every render while keeping the
  // deps-list lint rule happy (unlike useMemo with an empty array).
  const initialStateRef = useRef<QuestionnaireState | null>(null);
  if (initialStateRef.current === null) {
    initialStateRef.current = computeInitialQuestionnaireState(
      sections,
      validations,
      config,
    );
  }

  // Hydrate the atom synchronously (only takes effect on the first render).
  // atomWithReducer requires dispatching an action — HYDRATE replaces the full state.
  useHydrateAtoms([
    [
      questionnaireReducerAtom,
      { type: ACTIONS.HYDRATE, payload: initialStateRef.current },
    ],
  ]);

  // Dispatch updates only when props change after the initial mount
  const actions = useQuestionnaireActions();
  const prevConfig = useRef(config);
  const prevSections = useRef(sections);
  const prevValidations = useRef(validations);

  useEffect(() => {
    if (config !== prevConfig.current && Object.keys(config).length) {
      actions.setConfig(config);
    }
    if (sections !== prevSections.current && sections) {
      actions.setSections(sections);
    }
    if (
      validations !== prevValidations.current &&
      validations &&
      Object.keys(validations).length
    ) {
      actions.setValidations(validations);
    }

    prevConfig.current = config;
    prevSections.current = sections;
    prevValidations.current = validations;
  }, [config, sections, validations, actions]);
};
